import { useEffect, useState } from 'react'
import { Card, Row, Col, Statistic, Table, Button, Space, Spin, Tag, Progress } from 'antd'
import {
  ShoppingOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  UserAddOutlined,
  SyncOutlined,
  EyeOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { dashboardAPI } from '../../api'

interface DashboardData {
  totalProducts: number
  totalOrders: number
  totalUsers: number
  totalRevenue: number
  pendingOrders: number
  newCustomers: number
  repeatCustomers: number
  todayVisitors: number
  salesGrowth: number
  ordersGrowth: number
  customersGrowth: number
  repurchaseRate: number
  trendData: TrendItem[]
  recentOrders: RecentOrder[]
  pendingTasks: PendingTask[]
}

interface TrendItem {
  date: string
  gmv: number
  orders: number
  visitors: number
}

interface RecentOrder {
  id: number
  orderNumber: string
  customerName: string
  amount: number
  status: string
  createdAt: string
}

interface PendingTask {
  id: number
  type: string
  title: string
  count: number
  priority: 'high' | 'medium' | 'low'
}

const Dashboard = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DashboardData>({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    newCustomers: 0,
    repeatCustomers: 0,
    todayVisitors: 0,
    salesGrowth: 0,
    ordersGrowth: 0,
    customersGrowth: 0,
    repurchaseRate: 0,
    trendData: [],
    recentOrders: [],
    pendingTasks: [],
  })

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const today = dayjs()
      const startDate = today.subtract(7, 'day').format('YYYY-MM-DD')
      const endDate = today.format('YYYY-MM-DD')

      const [statsRes, trendRes, tasksRes, ordersRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getSalesTrend({ range: '7days', startDate, endDate }),
        dashboardAPI.getPendingTasks(),
        dashboardAPI.getRecentOrders(5),
      ])

      if (statsRes.code === 200) {
        const s = statsRes.data
        setData(prev => ({
          ...prev,
          totalProducts: s.totalProducts || 0,
          totalOrders: s.totalOrders || 0,
          totalUsers: s.totalUsers || 0,
          totalRevenue: s.totalRevenue || 0,
          pendingOrders: s.pendingOrders || 0,
          newCustomers: s.newCustomers || 0,
          repeatCustomers: s.repeatCustomers || 0,
          todayVisitors: s.todayVisitors || 0,
          salesGrowth: s.salesGrowth || 0,
          ordersGrowth: s.ordersGrowth || 0,
          customersGrowth: s.customersGrowth || 0,
          repurchaseRate: s.repurchaseRate || 0,
        }))
      }

      if (trendRes.code === 200) {
        setData(prev => ({ ...prev, trendData: trendRes.data || [] }))
      }

      if (tasksRes.code === 200) {
        setData(prev => ({ ...prev, pendingTasks: tasksRes.data || [] }))
      }

      if (ordersRes.code === 200) {
        setData(prev => ({ ...prev, recentOrders: ordersRes.data || [] }))
      }
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const statusMap: Record<string, { color: string; text: string }> = {
    pending: { color: 'orange', text: '待付款' },
    paid: { color: 'blue', text: '待发货' },
    delivered: { color: 'cyan', text: '已发货' },
    completed: { color: 'green', text: '已完成' },
    cancelled: { color: 'red', text: '已取消' },
  }

  const orderColumns = [
    { title: '订单号', dataIndex: 'orderNumber', key: 'orderNumber', width: 160 },
    { title: '客户', dataIndex: 'customerName', key: 'customerName' },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => val != null ? `¥${val.toFixed(2)}` : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (val: string) => (
        <Tag color={statusMap[val]?.color}>{statusMap[val]?.text || val}</Tag>
      ),
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-',
    },
  ]

  const priorityMap: Record<string, { color: string }> = {
    high: { color: 'red' },
    medium: { color: 'orange' },
    low: { color: 'blue' },
  }

  const goToOrders = () => navigate('/orders')

  return (
    <div className="dashboard">
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card>
              <Statistic title="商品总数" value={data.totalProducts} prefix={<ShoppingOutlined />} valueStyle={{ color: '#006835' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card>
              <Statistic title="累计订单" value={data.totalOrders} prefix={<ShoppingCartOutlined />} valueStyle={{ color: '#006835' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card>
              <Statistic title="累计用户" value={data.totalUsers} prefix={<UserOutlined />} valueStyle={{ color: '#006835' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card>
              <Statistic
                title="累计销售额"
                value={data.totalRevenue}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title="待办工单" value={data.pendingOrders} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title="新购客户" value={data.newCustomers} prefix={<UserAddOutlined />} valueStyle={{ color: '#52c41a' }} suffix={<span style={{ fontSize: 14 }}>近30日</span>} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title="复购客户" value={data.repeatCustomers} prefix={<SyncOutlined />} valueStyle={{ color: '#1890ff' }} suffix={<span style={{ fontSize: 14 }}>近30日</span>} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title="今日访问" value={data.todayVisitors} prefix={<EyeOutlined />} valueStyle={{ color: '#722ed1' }} suffix="UV" />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
            <Card title="待办工单" size="small">
              {data.pendingTasks.map(task => (
                <div key={task.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span>
                    <Tag color={priorityMap[task.priority]?.color}>
                      {task.priority === 'high' ? '紧急' : task.priority === 'medium' ? '中等' : '一般'}
                    </Tag>
                    {task.title}
                  </span>
                  <span style={{ fontWeight: 'bold', fontSize: 18, color: '#006835' }}>{task.count}</span>
                </div>
              ))}
              <div style={{ marginTop: 16 }}>
                <Button type="link" block onClick={goToOrders}>查看全部工单 →</Button>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title="核心指标趋势" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="gmv" name="GMV(元)" stroke="#cf1322" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="orders" name="订单数" stroke="#006835" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
            <Card title="客户复购率" size="small">
              <Row align="middle">
                <Col span={8}>
                  <Statistic title="复购率" value={data.repurchaseRate} precision={1} suffix="%" valueStyle={{ color: '#52c41a', fontSize: 32 }} />
                </Col>
                <Col span={16}>
                  <Progress percent={data.repurchaseRate} strokeColor="#52c41a" strokeWidth={20} showInfo={false} />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title="最近订单" size="small" extra={<Button type="link" onClick={goToOrders}>查看全部 →</Button>}>
              <Table columns={orderColumns} dataSource={data.recentOrders} rowKey="id" pagination={false} size="small" />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  )
}

export default Dashboard
